import { MobileNavButton } from "~/feature/CategoryNavigation/v1/MobileNavButton";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import {
  backdropClasses,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Drawer,
  drawerClasses,
  Fade,
  IconButton,
  MenuItem,
  MenuList,
  styled,
} from "@mui/material";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import type { RootState } from "~/redux/store";
import { useSelector } from "react-redux";
import type { NavigationNode, UsefulTool } from "~/Header/types";
import type React from "react";
import { type CSSProperties, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { C_ON_SURFACE, C_PRIMARY, C_SALES_CAMPAIGN } from "~/common/components/icon/Colors";
import { ArrowForward, ArrowRight, ChevronLeft, Close } from "@mui/icons-material";
import { sendNavigationLinkEvent } from "~/feature/Analytics/ga/ga";
import classNames from "classnames";
import { LinkButton } from "~/common/components/Link";
import { AVAILABLE_LANGUAGES } from "~/utils/constants";
import { LanguageSelector } from "./components/LanguageSelector";
import useMediaQuery, { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { UsefulToolsSection } from "~/feature/CategoryNavigation/v2/UsefulToolsSection";
import { SALES_CAMPAIGN_PAGE } from "~/utils/url/staticUrls";
import { useActiveCampaignsNotInSmartEdit } from "~/Header/components/TopBannersSection/utils";

const FADE_TIMEOUT = 200;

function pickUsefulTools(navigationTransitionNodes: TreeNode[]) {
  // Starting with the last node, find the first usefulTools array;
  return (
    navigationTransitionNodes
      .slice()
      .reverse()
      .find((node) => node.usefulTools.length > 0)?.usefulTools ?? []
  );
}

export const CategoryNavigation = ({ navigationRef }: { navigationRef: React.RefObject<HTMLElement> }) => {
  const { t } = useTranslation();
  const desktopNavRef = useRef<HTMLDivElement>(null);

  let delay = FADE_TIMEOUT;
  const urlParams = new URLSearchParams(window.location.search);
  const navDelay = urlParams.get("navDelay");

  if (navDelay !== null) {
    delay = parseInt(navDelay, 10);
  }

  const screenSize = useNumericScreenSize();

  const navigationNodes = useSelector(selectNavigationNodes);
  const rootNode = useSelector(selectRootNode);
  const tree = useMemo(() => {
    return buildNavigationTree(rootNode, navigationNodes);
  }, [navigationNodes, rootNode]);

  const { selectedNodes, nodesInTransition, isOpen, selectNode, goBack, flushTransitionNodes, close, toggleOpen } =
    useNavigationState(tree);
  const { onMouseEnter, onMouseLeave } = useHoverSelection(selectNode, delay);

  let anchor: "left" | "bottom" | "top" = "bottom";
  if (screenSize === useNumericScreenSize.tablet) {
    anchor = "left";
  } else if (screenSize > useNumericScreenSize.tablet) {
    anchor = "top";
  }

  const isDesktop = screenSize >= useNumericScreenSize.desktopS;

  const offsetStyles = useDesktopDrawerOffsets(navigationRef, desktopNavRef);

  const navigationSelectedNodes = isDesktop ? selectedNodes.slice(1) : selectedNodes;
  const navigationTransitionNodes = isDesktop ? nodesInTransition.slice(1) : nodesInTransition;
  const isVisible = (node: TreeNode) => {
    return isDesktop
      ? navigationTransitionNodes.includes(node)
      : navigationTransitionNodes[navigationTransitionNodes.length - 1] === node;
  };

  const isSelected = (node: TreeNode) => {
    return navigationTransitionNodes.includes(node);
  };

  const showLanguageSelector = AVAILABLE_LANGUAGES.length > 1 && selectedNodes.length <= 1;

  const isHoverEnabled = useMediaQuery("(hover: hover)");

  let usefulTools: UsefulTool[] = [];
  if (isDesktop) {
    usefulTools = pickUsefulTools(navigationTransitionNodes);
  }

  return (
    <ClickAwayListener
      onClickAway={close}
      mouseEvent={isDesktop ? undefined : false}
      touchEvent={isDesktop ? undefined : false}
    >
      <div ref={desktopNavRef}>
        {!isDesktop && <MobileNavButton onClick={toggleOpen} icon={isOpen ? IconType.Close : IconType.List} />}
        {isDesktop && (
          <DesktopTopLevelCategoryNavigation
            style={offsetStyles}
            node={tree}
            onClick={selectNode}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isSelected={isSelected}
          />
        )}
        <NavigationDrawer
          open={isOpen}
          anchor={anchor}
          onClose={close}
          style={offsetStyles}
          SlideProps={{ appear: false, exit: false, enter: false }}
        >
          {!isDesktop && (
            <DrawerHeader>
              <Fade in={nodesInTransition.length > 1} timeout={delay}>
                <BackButton onClick={goBack} startIcon={<ChevronLeft />} variant={"text"}>
                  {t("generic.back")}
                </BackButton>
              </Fade>
              <IconButton size="small" variant={"contained"} color={"secondary"} onClick={close}>
                <Close />
              </IconButton>
            </DrawerHeader>
          )}

          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <NavigationLevels>
              {navigationSelectedNodes.map((node) => (
                <Fade key={node.id} in={isVisible(node)} timeout={delay} onExited={flushTransitionNodes}>
                  <NavigationLevel
                    style={{ "--desktop-column": node.level + 1 } as CSSProperties}
                    tracking-aux-navigation={"true"}
                  >
                    {node.level >= 0 && (
                      <>
                        <NavigationLevelTitle
                          href={node.url}
                          target={node.openInNewTab ? "_blank" : undefined}
                          tracking-aux-navigation-name={`Overview ${t(node.title, { lng: "en" })}`}
                          tracking-aux-navigation-url={node.url}
                          tracking-aux-navigation-level={`Level ${node.level + 1}`}
                        >
                          <span>{t(node.title)}</span>
                          <ArrowForward />
                        </NavigationLevelTitle>
                        <Divider sx={{ marginBlock: "8px" }} />
                      </>
                    )}
                    <MenuList>
                      {!isHoverEnabled && node.url && (
                        <a href={node.url}>
                          <NavigationItem>
                            <span>{t("notifications.viewAll")}</span>
                          </NavigationItem>
                        </a>
                      )}
                      {node.childNodes.map((childNode) => (
                        <CategoryNavigationItem
                          key={childNode.id}
                          node={childNode}
                          selected={isSelected(childNode)}
                          onClick={selectNode}
                          onMouseEnter={onMouseEnter}
                          onMouseLeave={onMouseLeave}
                        />
                      ))}
                    </MenuList>
                  </NavigationLevel>
                </Fade>
              ))}
            </NavigationLevels>
            {usefulTools.length > 0 && (
              <UsefulToolsSection
                usefulTools={usefulTools}
                navigationLevel={navigationTransitionNodes[navigationTransitionNodes.length - 1]}
              />
            )}
          </Box>

          {!isDesktop && (
            <DrawerFooter>
              <Divider sx={{ marginTop: "8px", marginBottom: "16px" }} />
              <LanguageSelector />
            </DrawerFooter>
          )}
        </NavigationDrawer>
      </div>
    </ClickAwayListener>
  );
};

const NavigationDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
    [theme.breakpoints.up("desktop-s")]: {
      top: "var(--drawer-offset, 0px)",
      maxHeight: "calc(100dvh - 16px - var(--drawer-offset, 0px))",
      left: "calc(var(--drawer-left-offset, 0px) - 16px - 12px)",
      right: "unset",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    top: "var(--drawer-offset, 0px)",
  },
  [`.${backdropClasses.root}`]: {
    [theme.breakpoints.up("desktop-s")]: {
      top: "var(--drawer-offset, 0px)",
    },
  },
}));

const DrawerFooter = styled("div")(() => ({
  marginTop: "auto",
  padding: "16px",
  paddingTop: 0,
}));

const NavigationLevels = styled("div")(({ theme }) => ({
  position: "relative",
  display: "grid",
  overflow: "hidden",
  padding: "16px",
  width: "100%",

  [theme.breakpoints.down("desktop-s")]: {
    "& > *": {
      gridRow: "1",
      gridColumn: "1",
    },
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridAutoColumns: "288px",
    gridAutoFlow: "column",
    gap: "16px",
  },
  [theme.breakpoints.up("desktop-m")]: {
    gridAutoColumns: "358px",
  },
}));

const NavigationLevel = styled("div", {
  shouldForwardProp: () => true,
})(({ theme }) => ({
  position: "relative",
  overflow: "auto",

  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "var(--desktop-column, 1)",
    gridRow: 1,
  },
}));

const NavigationLevelTitle = styled("a", {
  shouldForwardProp: () => true,
})({
  padding: "8px 12px",
  display: "inline-flex",
  gap: "8px",
  color: C_ON_SURFACE,
  ...getFontStyle(FontSize.TitleMedium),

  ["&:hover"]: {
    textDecoration: "underline",
    color: C_PRIMARY,
    background: "transparent",
  },
});

const NavigationItem = styled(MenuItem)({
  ...getFontStyle(FontSize.BodyLarge),
  padding: "8px 12px",

  display: "flex",
  gap: "12px",
  alignItems: "center",
  width: "100%",
  minWidth: 0,
  whiteSpace: "pre-wrap",
  color: C_ON_SURFACE,

  ["&:hover"]: {
    textDecoration: "underline",
    color: C_PRIMARY,
    background: "transparent",
  },

  ["&.Mui-selected"]: {
    textDecoration: "underline",
    color: C_PRIMARY,
    background: "none",

    ["&:hover"]: {
      background: "transparent",
    },
  },
  ["> img.icon"]: {
    width: 24,
    height: 24,
    marginRight: -4,
    objectFit: "cover",
  },
});

const BackButton = styled(Button)({
  color: C_PRIMARY,

  ["&:hover"]: {
    background: "transparent",
    textDecoration: "underline",
  },
});

const DrawerHeader = styled("div")({
  color: C_PRIMARY,
  paddingRight: "12px",
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  ...getFontStyle(FontSize.BodyLarge),
  padding: "16px 16px 0 16px",
  marginBottom: "-8px",
});

const selectNavigationNodes = (state: RootState) => {
  return state.navigation.categoryNavigation.nodes;
};
const selectRootNode = (state: RootState) => {
  return state.navigation.categoryNavigation.root;
};

export interface TreeNode extends NavigationNode {
  childNodes: TreeNode[];
}

function useDesktopDrawerOffsets(
  navigationRef: React.RefObject<HTMLElement>,
  desktopNavRef: React.RefObject<HTMLDivElement>,
) {
  const parentBox = navigationRef.current?.getBoundingClientRect();
  const desktopNavigationBox = desktopNavRef.current?.getBoundingClientRect();

  const parentTop = parentBox?.top ?? 0;
  const parentHeight = parentBox?.height ?? 0;
  const navHeight = desktopNavigationBox?.height ?? 0;
  const navLeft = desktopNavigationBox?.left ?? 0;

  const drawerOffset = parentTop + parentHeight;
  const barOffset = (parentHeight - navHeight) / 2;

  const divStyle = {
    "--drawer-offset": `${drawerOffset}px`,
    "--drawer-left-offset": `${navLeft}px`,
    "--bar-offset": `${barOffset}px`,
  } as CSSProperties;
  return divStyle;
}

export function buildNavigationTree(root: NavigationNode, nodes: Record<number, NavigationNode>): TreeNode {
  const buildNode = (node: NavigationNode): TreeNode => {
    return {
      ...node,
      childNodes: node.children
        .map((childId) => nodes[childId])
        .filter((childNode): childNode is NavigationNode => childNode !== undefined)
        .filter((childNode) => childNode.level <= 3)
        .map((childNode) => buildNode(childNode)),
    };
  };

  return buildNode(root);
}

const DesktopTopLevelCategoryNavigation = ({
  node,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected,
  style,
}: {
  node: TreeNode;
  onClick: (node: TreeNode) => void;
  onMouseEnter: (node: TreeNode) => void;
  onMouseLeave: (node: TreeNode) => void;
  isSelected: (node: TreeNode) => boolean;
  style?: React.CSSProperties;
}) => {
  const { t } = useTranslation();
  const campaignsNotInSmartEdit = useActiveCampaignsNotInSmartEdit();

  return (
    <DesktopNavigationMenu style={style}>
      {node.childNodes.map((item, index) => {
        const isSaleTab = item.gaLabel === "Sale %";
        if (isSaleTab && !campaignsNotInSmartEdit.length) {
          return null;
        }

        return (
          <DesktopNavigationMenuItem
            key={index}
            data-test-id={`header-navigation-l0-${index}`}
            // link will be updated to multi campaign page in future
            onClick={
              isSaleTab
                ? () => {
                    window.location.href = SALES_CAMPAIGN_PAGE;
                  }
                : () => onClick(item)
            }
            onMouseEnter={isSaleTab ? undefined : () => onMouseEnter(item)}
            onMouseLeave={isSaleTab ? undefined : () => onMouseLeave(item)}
            className={classNames(isSelected(item) && "selected")}
          >
            <div className={classNames(isSaleTab && "saleTab")}>{t(item.title)}</div>
          </DesktopNavigationMenuItem>
        );
      })}
    </DesktopNavigationMenu>
  );
};

const DesktopNavigationMenu = styled("nav")(() => ({
  display: "inline-flex",
  gap: "32px",
}));

const DesktopNavigationMenuItem = styled(LinkButton)(() => ({
  ...getFontStyle(FontSize.BodyLarge),
  display: "inline-flex",
  color: C_ON_SURFACE,
  position: "relative",

  ["&.selected"]: {
    color: C_PRIMARY,

    ["&::after"]: {
      content: '""',
      position: "absolute",
      bottom: "calc(-1 * var(--bar-offset, 0px))",
      left: "0",
      width: "100%",

      borderBottom: `3px solid ${C_PRIMARY}`,
    },
  },

  ".saleTab": {
    color: C_SALES_CAMPAIGN,
  },
}));

const CategoryNavigationItem = ({
  node,
  selected,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  node: TreeNode;
  selected: boolean;
  onClick?: (node: TreeNode) => void;
  onMouseEnter?: (node: TreeNode, noChildren?: boolean) => void;
  onMouseLeave?: (node: TreeNode) => void;
}) => {
  const { t } = useTranslation();

  const isHoverEnabled = useMediaQuery("(hover: hover)");
  const isMinDesktop = useIsMinDesktopSize();
  const hasChildren = node.childNodes.length > 0;

  const props =
    (isHoverEnabled && isMinDesktop) || !hasChildren
      ? {
          component: "a",
          href: node.url,
          target: node.openInNewTab ? "_blank" : undefined,
          onMouseEnter: () => onMouseEnter?.(node, !hasChildren),
          onMouseLeave: hasChildren ? () => onMouseLeave?.(node) : undefined,
        }
      : {
          onClick: () => onClick?.(node),
        };

  return (
    <NavigationItem
      {...props}
      selected={selected}
      tracking-aux-navigation-name={t(node.title, { lng: "en" })}
      tracking-aux-navigation-url={node.url}
      tracking-aux-navigation-level={`category level ${node.level}`}
    >
      {node.smallImage && <img className={"icon"} src={node.smallImage.url} alt={node.smallImage.altText ?? ""} />}
      <span>{t(node.title)}</span>
      {node.childNodes.length > 0 && <ArrowRight sx={{ marginLeft: "auto" }} />}
    </NavigationItem>
  );
};

const useNavigationState = (rootNode: TreeNode) => {
  const { val: isOpen, setFalse: close, setTrue: open, toggle } = useBooleanState(false);
  const [selectedNodes, setSelectedNodes] = useState<TreeNode[]>([rootNode]);
  const [nodesInTransition, setNodesInTransition] = useState<TreeNode[]>([rootNode]);

  const selectNode = (node: TreeNode, noChildren = false) => {
    const lastNode = nodesInTransition[nodesInTransition.length - 1];

    if (!isOpen) {
      open();
    }

    if (!noChildren) {
      sendNavigationLinkEvent(node.gaLabel, node.level);

      setSelectedNodes([...nodesInTransition, node]);
    } else if (lastNode.level >= node.level) {
      setSelectedNodes([...nodesInTransition.slice(0, -1)]);
    }

    if (node.childNodes.length !== 0) {
      setNodesInTransition((n) => {
        const targetIndex = node.level + 1;
        const copy = n.slice(0, targetIndex);
        copy.push(node);
        return copy;
      });
    }
  };

  const goBack = () => {
    if (nodesInTransition.length > 1) {
      setNodesInTransition(nodesInTransition.slice(0, -1));
    }
  };

  const clearNodes = () => {
    setNodesInTransition([rootNode]);
    setSelectedNodes([rootNode]);
  };

  const handleTransitionEnd = () => {
    setSelectedNodes(nodesInTransition);
  };

  const handleClickOutside = () => {
    close();
    clearNodes();
  };

  return {
    selectedNodes,
    nodesInTransition,
    isOpen,
    selectNode,
    goBack,
    clearNodes,
    flushTransitionNodes: handleTransitionEnd,
    close: handleClickOutside,
    toggleOpen: toggle,
  };
};

const useHoverSelection = <T,>(selectValue: (node: T, noChildren?: boolean) => void, delay: number) => {
  const timeoutRef = useRef<number>();

  const onMouseEnter = (node: T, noChildren = false) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      selectValue(node, noChildren);
    }, delay);
  };

  const onMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return {
    onMouseEnter,
    onMouseLeave,
  };
};
